import { useMemo } from 'react'
import useUsersProfile from './useUsersProfile'
import { useUsersProfilAddressOfResidence, useUsersProfilBillingAddress } from './useUsersProfileAddresses'
import { useUserBankAccount } from './useUserBankAccount'

const PERSONAL_DATA_CONFIRMATION_DISMISSED_KEY = 'personalDataConfirmationDismissed'

export const usePersonalDataCompleteness = () => {
  const { data: profileData, isLoading: isProfileLoading } = useUsersProfile()
  const { usersProfilAddressOfResidence } = useUsersProfilAddressOfResidence()
  const { usersProfilBillingAddress } = useUsersProfilBillingAddress()
  const { userBankAccount, isLoading: isBankLoading } = useUserBankAccount()

  const isLoading = isProfileLoading || isBankLoading

  const hasMissingData = useMemo(() => {
    if (isLoading) return false

    // Check if at least one ID document exists
    const hasIdDocument = !!(profileData?.idCard || profileData?.passportId || profileData?.residenceCardNumber)

    // Check if address of residence is complete
    const hasAddressOfResidence = !!(
      usersProfilAddressOfResidence?.street &&
      usersProfilAddressOfResidence?.city &&
      usersProfilAddressOfResidence?.zipcode &&
      usersProfilAddressOfResidence?.countryId
    )

    // Check if billing address is complete
    const hasBillingAddress = !!(
      usersProfilBillingAddress?.street &&
      usersProfilBillingAddress?.city &&
      usersProfilBillingAddress?.zipcode &&
      usersProfilBillingAddress?.countryId
    )

    const isPolishNationality =
      !profileData?.nationality || profileData.nationality.toUpperCase() === 'PL'
    const hasPeselIfRequired = isPolishNationality ? !!profileData?.pesel : true

    // Check if basic required data is complete
    const hasBasicData = !!(
      profileData?.firstName &&
      profileData?.lastName &&
      profileData?.mobile &&
      profileData?.email &&
      hasPeselIfRequired &&
      profileData?.nationality &&
      profileData?.dateOfBirth &&
      profileData?.placeOfBirth &&
      profileData?.formOfEmployment
    )

    // Check if bank account has IBAN (only required field)
    const hasBankAccount = !!(userBankAccount?.bankAccountIban || userBankAccount?.bankAccountData)

    // Show widget if ANY of these is missing
    return !hasIdDocument || !hasAddressOfResidence || !hasBillingAddress || !hasBasicData || !hasBankAccount
  }, [profileData, usersProfilAddressOfResidence, usersProfilBillingAddress, userBankAccount, isLoading])

  const shouldShowPersonalDataConfirmation = useMemo(() => {
    if (isLoading) return false
    if (!hasMissingData) return false

    // Check if user has already dismissed the dialog
    const isDismissed = localStorage.getItem(PERSONAL_DATA_CONFIRMATION_DISMISSED_KEY) === 'true'
    if (isDismissed) return false

    return true
  }, [isLoading, hasMissingData])

  const dismissPersonalDataConfirmation = () => {
    localStorage.setItem(PERSONAL_DATA_CONFIRMATION_DISMISSED_KEY, 'true')
  }

  const resetDismissal = () => {
    localStorage.removeItem(PERSONAL_DATA_CONFIRMATION_DISMISSED_KEY)
  }

  return {
    hasMissingData,
    shouldShowPersonalDataConfirmation,
    dismissPersonalDataConfirmation,
    resetDismissal,
    isLoading
  }
}
